/**
 * InterestSelectionStep — step 3 of the onboarding wizard.
 * Multi-select grid of feature interest cards.
 */

import { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

export const FEATURE_INTERESTS = [
  {
    id: 'document_intelligence',
    label: 'Document Intelligence',
    icon: '📄',
  },
  {
    id: 'workflow_automation',
    label: 'Workflow Automation',
    icon: '⚙️',
  },
  {
    id: 'ai_assistant',
    label: 'AI Assistant',
    icon: '✨',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: '🔌',
  },
  {
    id: 'analytics_reporting',
    label: 'Analytics & Reporting',
    icon: '📊',
  },
  {
    id: 'team_collaboration',
    label: 'Team Collaboration',
    icon: '👥',
  },
] as const;

interface InterestCardProps {
  id: string;
  label: string;
  icon: string;
  selected: boolean;
  isFirst: boolean;
  shouldFocus: boolean;
  onToggle: (id: string) => void;
}

function InterestCard({ id, label, icon, selected, isFirst, shouldFocus, onToggle }: InterestCardProps) {
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (shouldFocus && btnRef.current) {
      btnRef.current.focus();
    }
  }, [shouldFocus]);

  return (
    <button
      ref={isFirst ? btnRef : undefined}
      type="button"
      data-interest-card={isFirst ? 'first' : undefined}
      onClick={() => onToggle(id)}
      aria-pressed={selected}
      className={[
        'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 p-4 text-center',
        'transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-blue-500',
        selected
          ? 'border-blue-500 bg-blue-50 shadow-md'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm',
      ].join(' ')}
    >
      <span className="text-3xl" aria-hidden="true">{icon}</span>
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {selected && (
        <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500">
          <Check className="h-3 w-3 text-white" aria-hidden="true" />
        </span>
      )}
    </button>
  );
}

interface InterestSelectionStepProps {
  selected: string[];
  /** Set to true when validation fired and 0 interests selected — focuses first card. */
  focusFirstCard: boolean;
  error: string | null;
  onChange: (interests: string[]) => void;
}

export function InterestSelectionStep({
  selected,
  focusFirstCard,
  error,
  onChange,
}: InterestSelectionStepProps) {
  function toggle(id: string) {
    const next = selected.includes(id) ? selected.filter((i) => i !== id) : [...selected, id];
    onChange(next);
  }

  return (
    <div className="px-4 py-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">What are you most interested in?</h2>
        <p className="mt-1 text-sm text-gray-500">
          Select one or more areas. We'll customise your experience to match.
        </p>
      </div>

      {error && (
        <p className="mb-3 text-sm font-medium text-red-600" role="alert" aria-live="assertive">
          {error}
        </p>
      )}

      <div
        className={[
          'grid grid-cols-1 gap-3 rounded-lg sm:grid-cols-2 lg:grid-cols-3',
          error ? 'p-2 ring-2 ring-red-400' : '',
        ].join(' ')}
        role="group"
        aria-label="Feature interests"
      >
        {FEATURE_INTERESTS.map((interest, index) => (
          <InterestCard
            key={interest.id}
            id={interest.id}
            label={interest.label}
            icon={interest.icon}
            selected={selected.includes(interest.id)}
            isFirst={index === 0}
            shouldFocus={focusFirstCard && index === 0}
            onToggle={toggle}
          />
        ))}
      </div>

      <p className="mt-3 text-xs text-gray-400">
        {selected.length} of {FEATURE_INTERESTS.length} selected
      </p>
    </div>
  );
}
